// Iter 115g — client-side mirror of the backend SIP trace upload guard
// (backend/routers/_sip_guard.py). Gives instant feedback; the server remains
// the authority and rejects anything that slips past this.
export const EXPECTED_HEADER_HINT = "timestamp, event, direction, from, to, sip_code, leg, detail";

const SAFE_NAME = /^[A-Za-z0-9][A-Za-z0-9 ._()[\]-]*$/;

const CODE_PATTERNS = [
  [/<\?php|<\?=/i, "PHP code"],
  [/<script\b|<\/script>|javascript:|onerror\s*=|onload\s*=|<iframe\b/i, "HTML/JavaScript"],
  [/\bdocument\.(cookie|write|location)\b|\bconsole\.log\s*\(|XMLHttpRequest|\bfetch\s*\(/i, "JavaScript"],
  [/\beval\s*\(|\bexec\s*\(|\bsystem\s*\(|shell_exec|popen\s*\(|subprocess\./i, "code execution call"],
  [/^\s*#!\s*\/|\/bin\/(ba)?sh\b|\bcurl\s+-|\bwget\s+http/im, "shell script"],
  [/^\s*#include\s*[<"]|\bstd::|\bprintf\s*\(|\bvoid\s+main\s*\(/im, "C/C++"],
  [/\bpublic\s+(static\s+)?(void|class)\b|System\.out\.print|\bimport\s+java\./i, "Java"],
  [/^\s*def\s+\w+\s*\(|__import__|\bos\.popen\b/im, "Python"],
  [/\busing\s+System\b|Console\.WriteLine/i, "C#"],
  [/^\s*<%|\bResponse\.Write\b/im, "server-side template"],
  [/\b(UNION\s+SELECT|DROP\s+TABLE|DELETE\s+FROM|OR\s+1\s*=\s*1)\b/i, "SQL injection pattern"],
  [/\$\{[^}]*\}|\{\{[^}]*\}\}/, "template expression"],
  [/\bjndi:|\bldap:\/\/|\bdata:text\/html/i, "remote/URI payload"],
  // eslint-disable-next-line no-control-regex
  [/\x1b\[/, "ANSI escape sequence"],
];

const FAMILIES = {
  timestamp: ["timestamp", "time", "datetime", "date", "start_time", "starttime", "event_time", "ts", "call_start",
    "time_stamp", "unixtsm", "unix_tsm", "unixtimestamp", "unix_timestamp", "epoch"],
  event: ["event", "method", "message", "sip_method", "action", "type", "state", "status", "disposition"],
  from: ["from", "caller", "ani", "src", "source", "from_number", "calling", "caller_id", "orig", "origination"],
  to: ["to", "callee", "dnis", "did", "dst", "destination", "to_number", "called", "term", "termination"],
  code: ["sip_code", "code", "cause", "q850", "response", "status_code", "hangup_cause", "reason"],
  context: ["leg", "direction", "detail", "duration", "call_id", "callid", "endpoint", "extension", "trunk",
    "server", "user_agent", "talk_time", "ring_time", "jitter", "latency", "packet_loss", "codec",
    "text", "host", "index", "seq", "sequence", "src_ip", "dst_ip", "ip"],
};

// Raw-message traces (ProSBC / CNms exports) put the whole SIP packet, SDP body
// and debug dump inside one quoted, multi-line free-text column.
const RAW_TEXT_COLUMNS = ["text", "message", "detail", "raw", "packet", "body", "log"];

// Quote-aware CSV parse — required because raw-message cells span many lines.
function parseCsv(text, delim) {
  const rows = [];
  let row = [];
  let cell = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { cell += '"'; i += 1; } else inQuotes = false;
      } else cell += ch;
    } else if (ch === '"') inQuotes = true;
    else if (ch === delim) { row.push(cell); cell = ""; }
    else if (ch === "\n") { row.push(cell); rows.push(row); row = []; cell = ""; }
    else if (ch !== "\r") cell += ch;
  }
  row.push(cell);
  rows.push(row);
  return rows.filter((r) => r.some((c) => (c || "").trim()));
}

const norm = (s) => (s || "").trim().replace(/^["']|["']$/g, "").toLowerCase()
  .replace(/[\s-]+/g, "_").replace(/[^a-z0-9_]/g, "");

export function validateTraceName(name) {
  const raw = (name || "").trim();
  if (!raw) return "File name is missing.";
  if (raw.length > 120) return `${raw}: file name is too long (max 120 characters).`;
  // eslint-disable-next-line no-control-regex
  if (/[^\x20-\x7e]/.test(raw)) return `${raw}: file name must use plain ASCII characters only.`;
  if (raw.includes("/") || raw.includes("\\") || raw.includes("..") || raw.startsWith("."))
    return `${raw}: file name must not contain path separators or '..'.`;
  if ((raw.match(/\./g) || []).length !== 1 || !raw.toLowerCase().endsWith(".csv"))
    return `${raw}: only single-extension .csv files are accepted.`;
  if (!SAFE_NAME.test(raw))
    return `${raw}: file name has unsupported characters — use letters, numbers, spaces, dots, underscores or hyphens.`;
  return null;
}

export function validateTraceText(name, text) {
  const nameErr = validateTraceName(name);
  if (nameErr) return nameErr;
  if (!text || !text.trim()) return `${name}: the file is empty.`;
  if (text.includes("\0")) return `${name}: the file contains binary data.`;
  const head = text.slice(0, 200).trimStart();
  if (/^(%PDF|PK\u0003\u0004|<html|<!DOCTYPE|<\?xml|\{\\rtf|MZ)/i.test(head))
    return `${name}: this is not a plain-text CSV.`;
  for (const [rx, label] of CODE_PATTERNS) {
    const m = rx.exec(text);
    if (m) return `${name}: rejected — embedded ${label} detected ("${m[0].slice(0, 30)}").`;
  }
  const lines = text.replace(/\r\n?/g, "\n").split("\n").filter((l) => l.trim());
  if (lines.length < 2) return `${name}: the CSV needs a header row plus at least one data row.`;
  const counts = { ",": 0, ";": 0, "\t": 0, "|": 0 };
  Object.keys(counts).forEach((d) => { counts[d] = lines[0].split(d).length - 1; });
  const delim = Object.keys(counts).reduce((a, b) => (counts[b] > counts[a] ? b : a));
  if (counts[delim] === 0) return `${name}: no CSV columns found. Expected header: ${EXPECTED_HEADER_HINT}`;
  const rows = parseCsv(text.replace(/\r\n?/g, "\n"), delim);
  if (rows.length < 2) return `${name}: the CSV needs a header row plus at least one data row.`;
  const header = rows[0];
  if (header.length < 3 || header.length > 60)
    return `${name}: header has ${header.length} columns; expected 3–60. Expected header: ${EXPECTED_HEADER_HINT}`;
  const cells = new Set(header.map(norm));
  const matched = Object.entries(FAMILIES).filter(([, names]) => names.some((n) => cells.has(n))).map(([f]) => f);
  if (!matched.includes("timestamp"))
    return `${name}: no timestamp column found. Expected header: ${EXPECTED_HEADER_HINT}`;
  if (matched.filter((f) => f !== "timestamp").length < 2)
    return `${name}: header does not match the expected call-trace format. Expected header: ${EXPECTED_HEADER_HINT}`;
  const rawCols = new Set(header.map(norm).map((h, i) => (RAW_TEXT_COLUMNS.includes(h) ? i : -1)).filter((i) => i >= 0));
  for (let i = 1; i < rows.length; i += 1) {
    for (let j = 0; j < rows[i].length; j += 1) {
      const c = (rows[i][j] || "").trim();
      if (!c) continue;
      if (!rawCols.has(j) && (/^[=@]/.test(c) || (/^[+-]/.test(c) && !/^[+-][\d\s().-]*$/.test(c))))
        return `${name}: row ${i + 1} starts with a spreadsheet formula ("${c.slice(0, 20)}").`;
      if (/\b(dde|ddeauto|cmd\s*\||powershell|rundll32|mshta|wscript)\b/i.test(c))
        return `${name}: row ${i + 1} contains a command/DDE payload ("${c.slice(0, 30)}").`;
    }
  }
  return null;
}
